import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import MasterLayout from '../layouts/masterlayout';
import ErrorMessage from '../common/errormessage';
import SuccessMessage from '../common/successmessage';
import GeneralTextBox from '../common/textboxes/textbox_general';
import StateDropdown from '../common/dropdowns/dropdown_states';
import ZipCodeInput from '../common/textboxes/textbox_zipcode';
import GeneralButton from '../common/button_general';
import Spinner from '../common/spinner';
import { getHeaders, handleErrorResponse, handleResponse } from '../../lib/requests';

function ProviderForm({ client = {} }) {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [formData, setFormData] = useState({
    name: client.name || '',
    street: client.street || '',
    city: client.city || '',
    state: client.state || '',
    zip_code: client.zip_code || '',
    country: client.country || 'United States',
    phone: client.phone || '',
    meal_price: client.meal_price || 0, // Standard Meal Price
    therapeutic_meal_price: client.therapeutic_meal_price || 0,
    mechanical_surcharge: client.mechanical_surcharge || 0,
    secondary_phone: client.secondary_phone || '',
    primary_contact_name: client.primary_contact_name || '',
    secondary_contact_name: client.secondary_contact_name || '',
    primary_contact_email: client.primary_contact_email || '',
    secondary_contact_email: client.secondary_contact_email || '',
    billing_contact_name: client.billing_contact_name || '',
    secondary_billing_contact_name: client.secondary_billing_contact_name || '',
    billing_contact_email: client.billing_contact_email || '',
    secondary_billing_contact_email: client.secondary_billing_contact_email || '',
    delivery_days: Array.isArray(client.delivery_days) ? client.delivery_days : []
  });

  useEffect(() => {
    if (!clientId) return;
    const fetchClientData = async () => {
      setIsLoading(true);
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const apiToken = localStorage.getItem('accessToken');
        const response = await axios.get(`${apiUrl}/clients/${clientId}`, {
          headers: getHeaders(apiToken),
        });
        const { ...rest } = response.data.data;
        setFormData({
          ...rest,
          delivery_days: Array.isArray(rest.delivery_days)
            ? rest.delivery_days
            : JSON.parse(rest.delivery_days || '[]')
        });
        setShowError(false);
      } catch (error) {
        handleErrorResponse(error, navigate, setShowError, setErrorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchClientData();
  }, [clientId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({ ...prevState, [name]: value }));
  };

  const handleStateChange = (item) => {
    setFormData(prevState => ({ ...prevState, state: item.name }));
  };

  const deliveryDaysOptions = [
    { label: 'Su', value: 0 },
    { label: 'M', value: 1 },
    { label: 'Tu', value: 2 },
    { label: 'W', value: 3 },
    { label: 'Th', value: 4 },
    { label: 'F', value: 5 },
    { label: 'Sa', value: 6 }
  ];

  const handleDeliveryDayChange = (dayValue) => {
    setFormData((prevState) => {
      const updatedDays = (prevState.delivery_days || []).includes(dayValue)
        ? prevState.delivery_days.filter((day) => day !== dayValue)
        : [...prevState.delivery_days, dayValue];
      return { ...prevState, delivery_days: updatedDays };
    });
  };

  const resetForm = () => {
    setSuccessMessage('');
    setErrorMessage('');
    setShowError(false);
    setShowSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    resetForm();
    const apiUrl = process.env.REACT_APP_API_URL;
    const apiToken = localStorage.getItem('accessToken');
    const method = clientId ? 'put' : 'post';
    const url = `${apiUrl}/clients${clientId ? `/${clientId}` : ''}`;

    try {
      const response = await axios(url, {
        method,
        headers: getHeaders(apiToken),
        data: formData,
      });

      handleResponse(response,
        "Client",
        setShowSuccess,
        setSuccessMessage,
        setShowError,
        setErrorMessage,
        navigate,
        '/clients/:id');
    } catch (error) {
      handleErrorResponse(error, navigate, setShowError, setErrorMessage);
    }
  };

  const pageTitle = clientId ? `Edit ${formData.name}` : 'Create Provider';

  return (
    <MasterLayout pageTitle={pageTitle}>
      {showError && <ErrorMessage message={errorMessage} />}
      {showSuccess && <SuccessMessage message={successMessage} setShowSuccess={setShowSuccess} />}
      {isLoading ? <Spinner /> : (
        <form onSubmit={handleSubmit} className="p-4">
          <div className="grid gap-6 mb-6 md:grid-cols-2">
            <GeneralTextBox
              label="Provider Name" id="name" name="name"
              value={formData.name} onChange={handleChange}
              required={true}
            />
            <GeneralTextBox
              label="Reference ID" id="reference_id" name="reference_id"
              value={formData.reference_id} onChange={handleChange}
              required={false}
            />
          </div>
          <div className="grid gap-6 mb-6 md:grid-cols-5">
            <GeneralTextBox
              label="Street" id="street" name="street"
              value={formData.street} onChange={handleChange}
              required={true}
            />
            <GeneralTextBox
              label="City" id="city" name="city"
              value={formData.city} onChange={handleChange}
              required={false}
            />
            <StateDropdown
              onSelect={handleStateChange}
              selectedState={formData.state}
            />
            <ZipCodeInput
              value={formData.zip_code}
              onChange={handleChange}
            />
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Delivery Days</label>
              <div className="grid gap-2 md:grid-cols-7">
                {deliveryDaysOptions.map(day => (
                  <div key={day.value} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`day-${day.value}`}
                      name="delivery_days"
                      value={day.value}
                      checked={(formData.delivery_days || []).includes(day.value)}
                      onChange={() => handleDeliveryDayChange(day.value)}
                      className="mr-2"
                    />
                    <label htmlFor={`day-${day.value}`} className="text-sm font-medium text-gray-900 dark:text-white">
                      {day.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="grid gap-6 mb-6 md:grid-cols-3">
            <div>
              <GeneralTextBox
                label="Standard Meal Price" id="meal_price" name="meal_price" type="currency"
                value={formData.meal_price} onChange={handleChange}
              />
            </div>
            <div>
              <GeneralTextBox
                label="Therapeutic Meal Price" id="therapeutic_meal_price" name="therapeutic_meal_price" type="currency"
                value={formData.therapeutic_meal_price} onChange={handleChange}
              />
            </div>
            <div>
              <GeneralTextBox
                label="Mechanical Surcharge" id="mechanical_surcharge" name="mechanical_surcharge" type="currency"
                value={formData.mechanical_surcharge} onChange={handleChange}
              />
            </div>
          </div>
          <div className="grid gap-6 mb-6 md:grid-cols-5">
            <div>
              <GeneralTextBox
                label="Phone Number" id="phone" name="phone"
                value={formData.phone} onChange={handleChange}
              />
            </div>
            <GeneralTextBox
              label="Primary Contact Name" id="primary_contact_name" name="primary_contact_name"
              value={formData.primary_contact_name} onChange={handleChange}
            />
            <GeneralTextBox
              label="Primary Contact Email" id="primary_contact_email" name="primary_contact_email"
              value={formData.primary_contact_email} onChange={handleChange}
            />
            <GeneralTextBox
              label="Billing Contact Name" id="billing_contact_name" name="billing_contact_name"
              value={formData.billing_contact_name} onChange={handleChange}
            />
            <GeneralTextBox
              label="Billing Contact Email" id="billing_contact_email" name="billing_contact_email"
              value={formData.billing_contact_email} onChange={handleChange}
            />
          </div>
          <div className="grid gap-6 mb-6 md:grid-cols-5">
            <GeneralTextBox
              label="Secondary Phone Number" id="secondary_phone" name="secondary_phone"
              value={formData.secondary_phone} onChange={handleChange}
            />
            <GeneralTextBox
              label="Secondary Contact Name" id="secondary_contact_name" name="secondary_contact_name"
              value={formData.secondary_contact_name} onChange={handleChange}
            />
            <GeneralTextBox
              label="Secondary Contact Email" id="secondary_contact_email" name="secondary_contact_email"
              value={formData.secondary_contact_email} onChange={handleChange}
            />
            <GeneralTextBox
              label="Secondary Billing Contact Name" id="secondary_billing_contact_name" name="secondary_billing_contact_name"
              value={formData.secondary_billing_contact_name} onChange={handleChange}
            />
            <GeneralTextBox
              label="Secondary Billing Contact Email" id="secondary_billing_contact_email" name="secondary_billing_contact_email"
              value={formData.secondary_billing_contact_email} onChange={handleChange}
            />
          </div>
          <div className="flex justify-center mt-6">
            <GeneralButton type="submit" buttonType="primary">
              {clientId ? 'Update Provider' : 'Create Provider'}
            </GeneralButton>
            <GeneralButton type="button" onClick={() => navigate('/providers')} buttonType="secondary">
              Back to Provider List
            </GeneralButton>
          </div>
        </form>
      )}
    </MasterLayout>
  );
}

export default ProviderForm;
